import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Heart, MapPin, Camera, Calendar } from "lucide-react";
import { travelStats, travelPhotos, wishLocations } from "@/data/photos";

/**
 * 关于我们页面
 * 展示爱情故事、旅行足迹总结和未来心愿
 */
export default function AboutPage() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: "oklch(0.97 0.015 70)", paddingTop: 64 }}>
      {/* Header */}
      <div
        className="flex items-center"
        style={{
          padding: "var(--spacing-md) var(--spacing-lg)",
          gap: "var(--spacing-md)",
          backgroundColor: "oklch(1 0 0)",
          boxShadow: "var(--ds-shadow-sm)",
        }}
      >
        <Link
          to="/"
          className="flex items-center rounded-lg transition-colors hover:bg-muted"
          style={{ gap: "var(--spacing-xs)", padding: "var(--spacing-xs)" }}
        >
          <ArrowLeft className="w-4 h-4" style={{ color: "var(--muted-foreground)" }} />
        </Link>
        <div>
          <h1
            style={{
              fontFamily: "var(--font-handwritten)",
              fontSize: "var(--font-size-title)",
              color: "var(--foreground)",
            }}
          >
            关于我们
          </h1>
          <p style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
            每一段旅程，都是爱的注脚
          </p>
        </div>
      </div>

      <div className="container mx-auto" style={{ padding: "var(--spacing-3xl) var(--spacing-lg)" }}>
        {/* Story Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto text-center"
          style={{ marginBottom: "var(--spacing-3xl)" }}
        >
          <Heart
            className="w-10 h-10 mx-auto mb-4"
            style={{ color: "oklch(0.65 0.18 25)" }}
          />
          <h2
            style={{
              fontFamily: "var(--font-handwritten)",
              fontSize: "clamp(1.5rem, 4vw, 2.5rem)",
              color: "var(--foreground)",
              marginBottom: "var(--spacing-lg)",
            }}
          >
            我们的故事
          </h2>
          <p
            style={{
              fontSize: "var(--font-size-body)",
              color: "var(--muted-foreground)",
              lineHeight: 2,
            }}
          >
            从相识到相知，从相知到相守。
            <br />
            我们一起走过了 {travelStats.citiesVisited} 座城市，行程超过 {travelStats.totalDistance.toLocaleString()} 公里。
            <br />
            每一次旅行都是一段新的回忆，每一张照片都承载着我们的笑声与感动。
            <br />
            这个网站，是我们爱情的见证，也是未来旅程的起点。
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 max-w-3xl mx-auto"
          style={{ gap: "var(--spacing-lg)", marginBottom: "var(--spacing-3xl)" }}
        >
          {[
            { icon: MapPin, value: travelStats.citiesVisited, label: "城市", color: "oklch(0.62 0.18 35)" },
            { icon: Camera, value: travelStats.photosCount, label: "照片", color: "oklch(0.65 0.14 160)" },
            { icon: Calendar, value: travelStats.daysTogether, label: "天", color: "oklch(0.75 0.16 55)" },
            { icon: Heart, value: wishLocations.length, label: "心愿", color: "oklch(0.65 0.18 25)" },
          ].map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="flex flex-col items-center rounded-2xl"
                style={{
                  padding: "var(--spacing-xl)",
                  backgroundColor: "oklch(1 0 0)",
                  boxShadow: "var(--ds-shadow-md)",
                  gap: "var(--spacing-sm)",
                }}
              >
                <Icon className="w-6 h-6" style={{ color: stat.color }} />
                <span
                  className="font-bold"
                  style={{
                    fontSize: "clamp(1.5rem, 4vw, 2rem)",
                    color: "var(--foreground)",
                  }}
                >
                  {stat.value}
                </span>
                <span style={{ fontSize: "var(--font-size-label)", color: "var(--muted-foreground)" }}>
                  {stat.label}
                </span>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Cities visited */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto"
          style={{ marginBottom: "var(--spacing-3xl)" }}
        >
          <h3
            className="text-center mb-6"
            style={{
              fontFamily: "var(--font-handwritten)",
              fontSize: "var(--font-size-headline)",
              color: "var(--foreground)",
            }}
          >
            足迹遍布
          </h3>
          <div className="flex flex-wrap justify-center" style={{ gap: "var(--spacing-sm)" }}>
            {Array.from(new Set(travelPhotos.map((p) => p.city))).map((city) => (
              <motion.span
                key={city}
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center rounded-full"
                style={{
                  gap: "var(--spacing-xs)",
                  padding: "var(--spacing-xs) var(--spacing-md)",
                  backgroundColor: "oklch(0.62 0.18 35 / 0.1)",
                  color: "oklch(0.52 0.16 35)",
                  fontSize: "var(--font-size-label)",
                  border: "1px solid oklch(0.62 0.18 35 / 0.15)",
                }}
              >
                <MapPin className="w-3 h-3" />
                {city}
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* Wish list */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto"
        >
          <h3
            className="text-center mb-6"
            style={{
              fontFamily: "var(--font-handwritten)",
              fontSize: "var(--font-size-headline)",
              color: "var(--foreground)",
            }}
          >
            下一站，想去的地方
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: "var(--spacing-md)" }}>
            {wishLocations.map((wish, i) => (
              <motion.div
                key={wish.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="flex flex-col rounded-xl"
                style={{
                  padding: "var(--spacing-lg)",
                  backgroundColor: "oklch(1 0 0)",
                  boxShadow: "var(--ds-shadow-md)",
                  gap: "var(--spacing-sm)",
                  borderLeft: "4px solid oklch(0.55 0.10 240 / 0.5)",
                }}
              >
                <span
                  className="font-semibold"
                  style={{ fontSize: "var(--font-size-body)", color: "var(--foreground)" }}
                >
                  {wish.name}
                </span>
                <p style={{ fontSize: "var(--font-size-label)", color: "var(--muted-foreground)", lineHeight: 1.6 }}>
                  {wish.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
